import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const inviteMember = createAsyncThunk(
  'organization/inviteMember',
  async ({ organizationId, email }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v1/organizations/invite', {
        organizationId,
        email,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send invitation'
      );
    }
  }
);

export const acceptInvitation = createAsyncThunk(
  'organization/acceptInvitation',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/api/v1/organizations/invitation/${token}/accept`,
        { email }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to accept invitation'
      );
    }
  }
);

export const removeMember = createAsyncThunk(
  'organization/removeMember',
  async ({ organizationId, memberId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v1/organizations/remove-member', {
        organizationId,
        memberId,
      });
      return memberId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove member'
      );
    }
  }
);

export const revokePendingInvitation = createAsyncThunk(
  'organization/revokePendingInvitation',
  async ({ organizationId, memberId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/v1/organizations/revoke-invitation', {
        organizationId,
        memberId,
      });
      return memberId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to revoke invitation'
      );
    }
  }
);

export const getMembers = createAsyncThunk(
  'organization/getMembers',
  async ({ organizationId, includeRemoved = false }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/organizations/members/${organizationId}?includeRemoved=${includeRemoved}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch members'
      );
    }
  }
);

export const getMyOrganization = createAsyncThunk(
  'organization/getMyOrganization',
  async (_, { rejectWithValue }) => {
    try {
      console.log('[getMyOrganization thunk] Making API call');
      const response = await api.get('/api/v1/organizations/my-organization');
      console.log('[getMyOrganization thunk] Response:', response);
      console.log('[getMyOrganization thunk] Response data:', response.data);
      return response;
    } catch (error) {
      console.log('[getMyOrganization thunk] Error:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch organization'
      );
    }
  }
);

export const getUserOrganization = createAsyncThunk(
  'organization/getUserOrganization',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/v1/organizations/user-organization');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch user organization'
      );
    }
  }
);

const initialState = {
  organization: null,
  members: [],
  pendingInvitations: [],
  loading: false,
  error: null,
  success: false,
  invitationAccepted: false,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetInvitationStatus: (state) => {
      state.invitationAccepted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(inviteMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inviteMember.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(inviteMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to send invitation';
      })
      .addCase(acceptInvitation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.invitationAccepted = true;
        state.success = true;
        state.error = null;
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to accept invitation';
      })
      .addCase(removeMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.members = state.members.filter(
          (member) => member._id !== action.payload
        );
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to remove member';
      })
      .addCase(revokePendingInvitation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revokePendingInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.members = state.members.map((member) =>
          member._id === action.payload ? { ...member, status: 'removed' } : member
        );
      })
      .addCase(revokePendingInvitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to revoke invitation';
      })
      .addCase(getMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload.members || [];
        state.pendingInvitations = (action.payload.members || []).filter(
          (m) => m.status === 'pending'
        );
      })
      .addCase(getMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch members';
      })
      .addCase(getMyOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyOrganization.fulfilled, (state, action) => {
        console.log('[getMyOrganization reducer] Action payload:', action.payload);
        console.log('[getMyOrganization reducer] payload.organization:', action.payload?.organization);
        console.log('[getMyOrganization reducer] payload.members:', action.payload?.members);
        
        state.loading = false;
        state.organization = action.payload?.organization;
        state.members = action.payload?.members?.members || [];
        state.pendingInvitations = (action.payload?.members?.members || []).filter(
          (m) => m.status === 'pending'
        );
        
        console.log('[getMyOrganization reducer] State updated:', {
          organization: state.organization,
          members: state.members,
          pendingInvitations: state.pendingInvitations,
        });
      })
      .addCase(getMyOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch organization';
      })
      .addCase(getUserOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserOrganization.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.organizationInfo?.organization) {
          state.organization = action.payload.organizationInfo.organization;
        }
      })
      .addCase(getUserOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user organization';
      });
  },
});

export const { clearError, clearSuccess, resetInvitationStatus } =
  organizationSlice.actions;

console.log('[organization/slice] organizationSlice:', organizationSlice);
console.log('[organization/slice] organizationSlice.reducer:', organizationSlice.reducer);

export default organizationSlice.reducer;
